import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Play, Volume2 } from "lucide-react";

/**
 * TourHeroPlayer — publicly-accessible embed of the 2-minute narrated
 * /tour-pro walkthrough for the login and signup pages.
 *
 * Shows an editorial "play card" hero above the auth form. Clicking it
 * opens a full-screen modal that loads the actual /tour-pro route in an
 * iframe — same 2-minute narrated experience as visiting /tour-pro
 * directly, no code duplication. Users can close the modal and return
 * to signing in. No login required to watch — the tour route is fully
 * public.
 *
 * Google Play testers requested that the walkthrough be discoverable
 * from the login page without needing to sign up first.
 */
export default function TourHeroPlayer() {
  const [open, setOpen] = useState(false);

  // Lock body scroll while the tour modal is open
  useEffect(() => {
    if (open) {
      const original = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = original; };
    }
  }, [open]);

  // Allow Escape to close the modal (standard modal UX pattern)
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      {/* HERO PLAY CARD — sits above the auth form on /login and /signup.
          Editorial: charcoal background with a rose→gold gradient sheen and
          a large centered play button. Full-width on mobile, capped on
          desktop. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="auth-tour-play-card"
        className="group relative w-full aspect-[16/7] rounded-2xl overflow-hidden mb-4 bg-gradient-to-br from-[#181214] via-[#0F1012] to-[#16171A] border border-white/[0.06] hover:border-[#C9A961]/25 transition text-left"
      >
        {/* Diagonal shimmer accent — subtle motion cue */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.05]" style={{
          backgroundImage: "linear-gradient(115deg, transparent 42%, #F0E6D2 50%, transparent 58%)",
          backgroundSize: "220% 100%",
          animation: "shimmer 8s ease-in-out infinite",
        }} />

        {/* Rose radial glow behind the play button */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-44 h-44 rounded-full bg-[#B95F73]/12 blur-3xl pointer-events-none" />

        {/* Content — horizontal layout for compact height */}
        <div className="absolute inset-0 flex items-center justify-center gap-4 px-5">
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: [0.95, 1, 0.95] }}
            transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
            className="shrink-0 w-12 h-12 rounded-full bg-[#C9A961] flex items-center justify-center shadow-[0_16px_40px_-16px_rgba(201,169,97,0.55)] group-hover:scale-105 transition"
          >
            <Play className="w-5 h-5 text-[#16171A] ml-0.5" fill="currentColor" />
          </motion.div>
          <div className="min-w-0 text-left">
            <div className="font-display text-base font-light tracking-tight text-[#F0E6D2] leading-tight">
              Watch <em className="font-display-italic text-[#B95F73]">Spark</em> in 2 minutes.
            </div>
            <div className="mt-0.5 flex items-center gap-1.5 text-[9px] tracking-[0.18em] uppercase text-[#8C867C]">
              <Volume2 className="w-2.5 h-2.5" />
              <span>Narrated · No sign-in required</span>
            </div>
          </div>
        </div>

        {/* Subtle "PREVIEW" corner badge */}
        <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-[#0F1012]/70 backdrop-blur border border-white/[0.06] text-[8px] font-bold tracking-[0.18em] uppercase text-[#C9A961]">
          Product Tour
        </div>
      </button>

      {/* FULL-SCREEN MODAL — loads /tour-pro in an iframe so the exact
          same narrated experience plays. Users can dismiss with the
          × button in the corner or press Escape. */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[999] bg-[#0F1012]"
            data-testid="auth-tour-modal"
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="auth-tour-modal-close"
              aria-label="Close tour"
              className="absolute top-4 right-4 z-[1001] w-10 h-10 rounded-full bg-black/60 backdrop-blur flex items-center justify-center text-[#F0E6D2] hover:bg-black/80 active:scale-90 transition"
            >
              <X className="w-5 h-5" />
            </button>
            <iframe
              src="/tour-pro"
              title="Spark product tour"
              className="w-full h-full border-0 block"
              // Allow autoplay + fullscreen so the narrated audio can play
              // immediately after the user clicks the play button inside
              // the tour (browser autoplay policy still requires that
              // initial user gesture, which is why /tour-pro shows a
              // tap-to-start gate — that gate stays intact inside the
              // iframe and the modal-open click alone isn't enough).
              allow="autoplay; fullscreen; encrypted-media"
              data-testid="auth-tour-iframe"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
